import { planClock, type Plan, type Schedule } from "@69ly/shared";
import { useRef, type PointerEvent as ReactPointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { isPlayer, type PlanActor } from "../../lib/plan.ts";
import { GrenadeGlyph } from "../GrenadeGlyph.tsx";

/** A tick every this many seconds, depending on how long the timeline is. */
const tickStep = (length: number) => (length <= 20 ? 2 : length <= 45 ? 5 : length <= 90 ? 10 : 15);

/**
 * The plan on one timeline, docked under the map: a lane per player (and per
 * opponent) with their walks, holds and line-ups, the throws and how long each
 * lasts, the calls, the chapters; the playhead to drag.
 */
export function PlanTimeline({
  plan,
  schedule,
  length,
  elapsed,
  onSeek,
  focus,
  selectedAction,
  onSelectAction,
  actorName,
}: {
  plan: Plan;
  schedule: Schedule;
  /** Seconds shown: the window, or more when the plan runs past it. */
  length: number;
  elapsed: number;
  onSeek: (elapsed: number) => void;
  focus: string | null;
  selectedAction?: string | null;
  onSelectAction?: (actor: string, action: string) => void;
  actorName: (a: PlanActor) => string;
}) {
  const { t } = useTranslation();
  const tracks = useRef<HTMLDivElement>(null);
  const x = (e: number) => `${(Math.min(length, Math.max(0, e)) / length) * 100}%`;
  const w = (a: number, b: number) => `${(Math.max(0, Math.min(length, b) - Math.max(0, a)) / length) * 100}%`;
  const actors: PlanActor[] = [...plan.players, ...plan.opponents];
  const step = tickStep(length);
  const ticks = Array.from({ length: Math.floor(length / step) + 1 }, (_, i) => i * step);

  const seekAt = (clientX: number) => {
    const el = tracks.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    onSeek(((clientX - r.left) / r.width) * length);
  };
  const down = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    seekAt(e.clientX);
  };

  return (
    <section className={`plan-timeline${plan.chapters.length ? " has-chapters" : ""}`} aria-label={t("plan.timeline")}>
      <div className="plan-tl-names" aria-hidden="true">
        <div className="plan-tl-head" />
        {actors.map((a) => (
          <div key={a.id} className={`plan-tl-name${focus && focus !== a.id ? " is-dim" : ""}`}>
            <span className={`pp-dot ${isPlayer(a) ? `slot-${a.slot}` : "is-enemy"}`}>{isPlayer(a) ? a.slot : "E"}</span>
            <span className="plan-tl-label">{actorName(a)}</span>
          </div>
        ))}
      </div>
      <div
        ref={tracks}
        className="plan-tl-tracks"
        role="slider"
        tabIndex={0}
        aria-label={t("plan.playhead")}
        aria-valuemin={0}
        aria-valuemax={Math.round(length)}
        aria-valuenow={Math.round(elapsed)}
        aria-valuetext={planClock(schedule.toClock(elapsed))}
        onPointerDown={down}
        onPointerMove={(e) => e.currentTarget.hasPointerCapture(e.pointerId) && seekAt(e.clientX)}
        onPointerUp={(e) => e.currentTarget.releasePointerCapture(e.pointerId)}
        onKeyDown={(e) => {
          if (e.key === "ArrowRight") onSeek(elapsed + (e.shiftKey ? 5 : 0.5));
          if (e.key === "ArrowLeft") onSeek(elapsed - (e.shiftKey ? 5 : 0.5));
          if (e.key === "Home") onSeek(0);
          if (e.key === "End") onSeek(length);
        }}
      >
        <div className="plan-tl-head">
          {ticks.map((s) => (
            <span key={s} className="plan-tl-tick" style={{ left: x(s) }}>
              {planClock(schedule.toClock(s))}
            </span>
          ))}
          {plan.chapters.map((c) => (
            <span key={c.id} className="plan-tl-chapter" style={{ left: x(schedule.toElapsed(c.at)) }} title={`${c.name} · ${planClock(c.at)}`}>
              {c.name}
            </span>
          ))}
        </div>
        {actors.map((a) => {
          const s = schedule.actors.get(a.id);
          const lane = isPlayer(a) ? `slot-${a.slot}` : "is-enemy";
          return (
            <div key={a.id} className={`plan-tl-lane ${lane}${focus && focus !== a.id ? " is-dim" : ""}`}>
              {s?.segments.map((g, i) =>
                g.kind === "wait" ? null : (
                  <span
                    key={i}
                    className={`plan-tl-seg seg-${g.kind}${g.action && g.action === selectedAction ? " is-selected" : ""}`}
                    style={{ left: x(g.start), width: w(g.start, g.end) }}
                    title={`${t(`plan.segment.${g.kind}`)} · ${planClock(schedule.toClock(g.start))} → ${planClock(schedule.toClock(g.end))}`}
                    onPointerDown={() => g.action && onSelectAction?.(a.id, g.action)}
                  />
                ),
              )}
              {schedule.throws
                .filter((th) => th.actor === a.id)
                .map((th, i) => (
                  <span key={`th${i}`}>
                    <span className={`plan-tl-effect gl-${th.grenade}`} style={{ left: x(th.lands), width: w(th.lands, th.gone) }} title={t("plan.effectLasts", { title: th.title })} />
                    <span
                      className={`plan-tl-throw gl-${th.grenade}${th.action === selectedAction ? " is-selected" : ""}`}
                      style={{ left: x(th.at) }}
                      title={`${th.title} · ${planClock(schedule.toClock(th.at))}`}
                      onPointerDown={() => onSelectAction?.(a.id, th.action)}
                    >
                      <GrenadeGlyph grenade={th.grenade} size={10} />
                    </span>
                  </span>
                ))}
              {schedule.calls
                .filter((c) => c.actor === a.id)
                .map((c) => (
                  <span key={c.action} className="plan-tl-call" style={{ left: x(c.at) }} title={`“${c.text}” · ${planClock(schedule.toClock(c.at))}`} onPointerDown={() => onSelectAction?.(a.id, c.action)} />
                ))}
            </div>
          );
        })}
        {schedule.span < length && <div className="plan-tl-after" style={{ left: x(schedule.span), width: w(schedule.span, length) }} title={t("plan.pastWindow")} aria-hidden="true" />}
        {plan.chapters.map((c) => (
          <span key={`l${c.id}`} className="plan-tl-chapter-line" style={{ left: x(schedule.toElapsed(c.at)) }} aria-hidden="true" />
        ))}
        <span className="plan-tl-playhead" style={{ left: x(elapsed) }} aria-hidden="true" />
      </div>
    </section>
  );
}
